import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../ui';
import { purchasePackage, checkPaymentStatus, formatInr, formatDate } from './checkout';

/* ==================================================================
   CheckoutModal
   ------------------------------------------------------------------
   The full payment conversation in one dialog, so no screen renders
   its own half-version of it.

   The rule it enforces visually: "Payment successful" appears ONLY in
   the `success` state, which is reachable only from a 200 on
   /api/billing/verify. Razorpay's window closing takes the user to
   `verifying`, never to success — because at that moment nobody has
   checked anything yet.

   `unknown` is a real state, not a fallback. If the network drops
   during verification the payment may well have gone through, and
   telling the user it failed would invite a second payment. It offers
   to re-check instead.
   ================================================================== */

const COPY = {
  creating: {
    title: 'Preparing your order',
    sub: 'Setting up a secure payment with Razorpay.',
    tone: 'pending',
  },
  opening: {
    title: 'Opening payment',
    sub: 'Complete the payment in the Razorpay window.',
    tone: 'pending',
  },
  verifying: {
    title: 'Confirming your payment',
    sub: 'Verifying with our servers. This usually takes a couple of seconds — please don’t close this window.',
    tone: 'pending',
  },
  cancelled: {
    title: 'Payment cancelled',
    sub: 'You closed the payment window before it finished. Nothing has been charged.',
    tone: 'warn',
  },
  failed: {
    title: 'Payment not completed',
    sub: 'No credits have been added and nothing has been charged to your account.',
    tone: 'error',
  },
  unknown: {
    title: 'We could not confirm your payment',
    sub: 'The connection dropped while we were checking. If money left your account the credits will still be added — check the status before paying again.',
    tone: 'warn',
  },
};

function Spinner() {
  return <span className="bl-spin" aria-hidden="true" />;
}

function TickIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polyline className="bl-tick" points="20 6 9 17 4 12" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
    </svg>
  );
}

function StateIcon({ tone }) {
  if (tone === 'success') return <TickIcon />;
  if (tone === 'pending') return <Spinner />;
  return <AlertIcon />;
}

export default function CheckoutModal({
  open,
  pkg,
  onClose,
  onSuccess,
}) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [rechecking, setRechecking] = useState(false);

  // Guards a genuine double-charge: without it, a re-render or a second
  // click while the Razorpay window is open would start a parallel purchase.
  const runningRef = useRef(false);

  const start = useCallback(async () => {
    if (runningRef.current || !pkg) return;
    runningRef.current = true;
    setError(null);
    setResult(null);

    const outcome = await purchasePackage(pkg.key, (next, detail) => {
      setState(next);
      if (detail?.error) setError(detail.error);
    });

    runningRef.current = false;
    setPaymentId(outcome.paymentId || null);

    if (outcome.status === 'success') {
      setResult(outcome);
      setState('success');
      // Let the parent refresh its entitlements immediately — the number in
      // the dashboard header should not wait for the dialog to be dismissed.
      onSuccess?.(outcome);
    } else if (outcome.error) {
      setError(outcome.error);
    }
  }, [pkg, onSuccess]);

  // Kick off as soon as the dialog opens for a package. Razorpay's own window
  // is the confirmation step, so an extra "are you sure?" screen in front of it
  // would just be a click that tells the user nothing.
  useEffect(() => {
    if (open && pkg && state === 'idle') start();
  }, [open, pkg, state, start]);

  // Reset once closed so reopening for another package starts clean.
  useEffect(() => {
    if (!open) {
      setState('idle');
      setError(null);
      setResult(null);
      setPaymentId(null);
      setRechecking(false);
    }
  }, [open]);

  const recheck = async () => {
    if (!paymentId) return;
    setRechecking(true);
    try {
      const data = await checkPaymentStatus(paymentId);
      if (data.payment?.status === 'paid') {
        const recovered = { payment: data.payment, entitlements: data.entitlements };
        setResult(recovered);
        setState('success');
        onSuccess?.(recovered);
      } else {
        setState('failed');
        setError('That payment has not completed. Nothing has been charged — you can try again.');
      }
    } catch {
      setError('We still could not reach our servers. Please check your billing history in a few minutes.');
    } finally {
      setRechecking(false);
    }
  };

  const retry = () => {
    setState('idle'); // the effect above restarts the purchase
  };

  // A payment in flight must not be dismissed by a stray Escape or backdrop
  // click — the user would lose the only view of what is happening.
  const busy = state === 'creating' || state === 'opening' || state === 'verifying';

  if (!pkg) return null;

  const isJob = pkg.product === 'job';

  // ── Success ──
  if (state === 'success' && result) {
    const ent = result.entitlements || {};
    const remaining = isJob
      ? ent.credits_remaining
      : (ent.credits_remaining ?? 0) + (ent.free_remaining ?? 0);

    return (
      <Modal open={open} onClose={onClose} maxWidth={460} labelledBy="bl-checkout-title">
        <div className="bl-checkout">
          <div className="bl-checkout-icon bl-checkout-icon--success">
            <TickIcon />
          </div>
          <h3 id="bl-checkout-title">Payment successful</h3>
          <p className="bl-checkout-sub">
            Your <strong>{pkg.name}</strong> {isJob ? 'job package' : 'profile access'} is now active.
          </p>

          <dl className="bl-receipt">
            <div className="bl-receipt-row">
              <dt>{isJob ? 'Job posts purchased' : 'Profile credits purchased'}</dt>
              <dd className="bl-num">{pkg.credits}</dd>
            </div>
            <div className="bl-receipt-row">
              <dt>Available now</dt>
              <dd className="bl-num">{remaining}</dd>
            </div>
            <div className="bl-receipt-row">
              <dt>Valid for</dt>
              <dd>{pkg.validity_days} days</dd>
            </div>
            {ent.expires_at && (
              <div className="bl-receipt-row">
                <dt>Access until</dt>
                <dd>{formatDate(ent.expires_at)}</dd>
              </div>
            )}
            <div className="bl-receipt-row">
              <dt>Amount paid</dt>
              <dd className="bl-num">{formatInr(result.payment?.amount_paise ?? pkg.amount_paise)}</dd>
            </div>
          </dl>

          {result.alreadyProcessed && (
            <p className="bl-checkout-sub" style={{ marginTop: -8 }}>
              This payment had already been confirmed — you have not been charged twice.
            </p>
          )}

          <div className="bl-checkout-actions">
            <button type="button" className="bl-plan-cta" onClick={onClose}>
              {isJob ? 'Post a Job' : 'Find Students'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  const copy = COPY[state] || COPY.creating;

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      dismissible={!busy}
      showClose={!busy}
      maxWidth={440}
      labelledBy="bl-checkout-title"
    >
      <div className="bl-checkout">
        <div className={`bl-checkout-icon bl-checkout-icon--${copy.tone}`}>
          <StateIcon tone={copy.tone} />
        </div>

        {/* aria-live so a screen reader hears the transition from
            "confirming" to "successful" without having to re-read the dialog. */}
        <h3 id="bl-checkout-title" aria-live="polite">{copy.title}</h3>
        <p className="bl-checkout-sub">{error || copy.sub}</p>

        {busy && (
          <p className="bl-checkout-sub" style={{ fontSize: 12.5, marginTop: -12 }}>
            {pkg.name} · {formatInr(pkg.amount_paise)}
          </p>
        )}

        {!busy && (
          <div className="bl-checkout-actions">
            <button type="button" className="bl-plan-cta bl-plan-cta--ghost" onClick={onClose}>
              Close
            </button>
            {state === 'unknown' ? (
              <button type="button" className="bl-plan-cta" onClick={recheck} disabled={rechecking}>
                {rechecking ? 'Checking…' : 'Check payment status'}
              </button>
            ) : (
              <button type="button" className="bl-plan-cta" onClick={retry}>
                Try again
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
